import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import {
  AlertCircleIcon,
  ArrowRightIcon,
  ClipboardCheckIcon,
  LayoutGridIcon,
  PlusIcon,
  SpinnerIcon,
  TargetIcon,
  UsersIcon,
} from '@/components/icons';
import { ActionIcon } from '@/components/ui/ActionIcon';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Select } from '@/components/ui/Select';

import { Tabs, TabPanel } from '@/components/ui/Tabs';
import { dateKey, type DateRange } from '@/components/ui/date-picker-utils';
import { ROUTES } from '@/app/router/routes';
import { useAuth } from '@/features/auth/context/auth-context';
import { queryKeys } from '@/lib/query/query-keys';
import { dashboardApi, type DashboardFilters } from '../api/dashboard.api';
import { DashboardChartCard } from '../components/DashboardChartCard';
import {
  DashboardActivities,
  DashboardMetrics,
  DashboardRatios,
} from '../components/DashboardSummary';
import { downloadDashboard } from '../dashboard-utils';
import type { DashboardSection } from '../types';
import './DashboardPage.css';

const sections = [
  { value: 'overview' as const, label: 'Généralités', icon: <LayoutGridIcon /> },
  { value: 'candidates' as const, label: 'Candidats', icon: <UsersIcon /> },
  { value: 'actions' as const, label: 'Actions', icon: <ClipboardCheckIcon /> },
  { value: 'requirements' as const, label: 'Besoins', icon: <TargetIcon /> },
];
const copy: Record<
  DashboardSection,
  { title: string; subtitle: string; link: string; linkLabel: string }
> = {
  overview: {
    title: 'Une vue d’ensemble, pour avancer.',
    subtitle: 'Votre portefeuille, votre activité et les étapes clés de vos recrutements.',
    link: ROUTES.actionCreate,
    linkLabel: 'Créer une action',
  },
  candidates: {
    title: 'Les talents au cœur de votre activité.',
    subtitle: 'Acquisition, qualification et positionnement de votre vivier de candidats.',
    link: ROUTES.candidates,
    linkLabel: 'Voir les candidats',
  },
  actions: {
    title: 'Chaque échange fait avancer la relation.',
    subtitle: 'Suivez votre activité et la répartition de vos efforts commerciaux.',
    link: ROUTES.actions,
    linkLabel: 'Voir les actions',
  },
  requirements: {
    title: 'Vos opportunités, en perspective.',
    subtitle: 'Priorités, profils recherchés et potentiel de votre portefeuille de besoins.',
    link: ROUTES.requirements,
    linkLabel: 'Voir les besoins',
  },
};
const presetRange = (preset: string): DateRange => {
  const today = new Date();
  if (preset === 'all') return { from: '', to: '' };
  const from = new Date(today);
  if (preset === 'month') from.setDate(1);
  else if (preset === 'year') from.setMonth(0, 1);
  else from.setDate(today.getDate() - 89);
  return { from: dateKey(from), to: dateKey(today) };
};
const RefreshIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="1em"
    height="1em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    aria-hidden="true"
  >
    <path
      d="M20 7v5h-5M4 17v-5h5M19 11a7 7 0 0 0-12-5L4 9m16 6-3 3A7 7 0 0 1 5 13"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const DownloadIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="1em"
    height="1em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    aria-hidden="true"
  >
    <path d="M12 3v12m-4-4 4 4 4-4M5 16v4h14v-4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const DashboardPage = () => {
  const { user } = useAuth();
  const [search, setSearch] = useSearchParams();
  const rawSection = search.get('section');
  const section: DashboardSection =
    sections.find((item) => item.value === rawSection)?.value ?? 'overview';
  const [preset, setPreset] = useState('all');
  const [range, setRange] = useState<DateRange>({ from: '', to: '' });
  const [ownerId, setOwnerId] = useState('');
  const filters: DashboardFilters = {
    section,
    ...(range.from ? { from: range.from } : {}),
    ...(range.to ? { to: range.to } : {}),
    ...(ownerId ? { ownerId } : {}),
  };
  const dashboard = useQuery({
    queryKey: queryKeys.dashboard.section(filters),
    queryFn: () => dashboardApi.get(filters),
    staleTime: 60_000,
  });
  const owners = useQuery({
    queryKey: queryKeys.dashboard.owners(),
    queryFn: dashboardApi.owners,
    staleTime: 300_000,
  });
  const data = dashboard.data;
  const content = copy[section];
  const changeSection = (value: DashboardSection) =>
    setSearch((current) => {
      const next = new URLSearchParams(current);
      next.set('section', value);
      return next;
    });
  const scopeKey = `${section}:${range.from}:${range.to}:${ownerId}`;
  const isFiltered = Boolean(range.from || range.to || ownerId);
  return (
    <div className="dashboard-page">
      <header className="dash-heading">
        <div>
          <span className="dash-eyebrow">Tableau de bord</span>
          <h1>
            Bonjour {user?.firstName ?? ''}
            <span className="dash-heading__dot">.</span>
          </h1>
          <p>{content.subtitle}</p>
        </div>
        <div className="dash-heading__actions">
          <span className="dash-today">
            {new Date().toLocaleDateString('fr-FR', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={!data || dashboard.isFetching}
            onClick={() =>
              data &&
              downloadDashboard(
                data,
                owners.data?.find((owner) => owner.id === ownerId)?.label ??
                  'Tous les responsables',
              )
            }
          >
            <DownloadIcon />
            Exporter la vue
          </Button>
        </div>
      </header>
      <div className="dash-navigation">
        <Tabs
          id="dashboard"
          tabs={sections}
          value={section}
          onChange={changeSection}
          label="Sections du tableau de bord"
        />
        <span className="dash-live">
          <i />
          Données de la plateforme
        </span>
      </div>
      <div className="dash-filters">
        <div className="dash-period-presets" role="group" aria-label="Période du tableau de bord">
          {[
            { value: 'all', label: 'Toute la période' },
            { value: 'month', label: 'Ce mois' },
            { value: '90', label: '90 jours' },
            { value: 'year', label: 'Cette année' },
          ].map((item) => (
            <button
              type="button"
              key={item.value}
              aria-pressed={preset === item.value}
              onClick={() => {
                setPreset(item.value);
                setRange(presetRange(item.value));
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
        <DatePicker
          label="Période personnalisée"
          mode="flexible"
          value={range}
          onValueChange={(value) => {
            setRange(value);
            setPreset(value.from || value.to ? 'custom' : 'all');
          }}
        />
        <Select
          label="Responsable"
          placeholder={
            owners.isPending
              ? 'Chargement…'
              : owners.isError
                ? 'Chargement indisponible'
                : 'Tous les responsables'
          }
          value={ownerId}
          options={(owners.data ?? []).map((owner) => ({
            value: owner.id,
            label: owner.label,
          }))}
          disabled={owners.isPending || owners.isError}
          onChange={(event) => setOwnerId(event.target.value)}
        />
        <ActionIcon
          label="Actualiser le tableau de bord"
          disabled={dashboard.isFetching}
          onClick={() => {
            void dashboard.refetch();
            if (owners.isError) void owners.refetch();
          }}
        >
          {dashboard.isFetching ? <SpinnerIcon className="animate-spin" /> : <RefreshIcon />}
        </ActionIcon>
      </div>
      <div className="dash-scope">
        <span>
          {range.from || range.to
            ? 'Fiches créées et actions datées dans la période choisie.'
            : 'Tout l’historique jusqu’à aujourd’hui.'}
          {ownerId && ' Périmètre du responsable sélectionné.'}
        </span>
        {isFiltered && (
          <button
            type="button"
            className="dash-text-button"
            onClick={() => {
              setPreset('all');
              setRange({ from: '', to: '' });
              setOwnerId('');
            }}
          >
            Réinitialiser
          </button>
        )}
      </div>
      <TabPanel id={`dashboard-${section}-panel`} labelledBy={`dashboard-${section}`}>
        {dashboard.isPending ? (
          <div className="dash-loading" role="status" aria-label="Chargement des indicateurs">
            <div className="dash-loading__metrics">
              {Array.from({ length: 6 }, (_, i) => (
                <span key={i} />
              ))}
            </div>
            <div className="dash-loading__charts">
              <span />
              <span />
            </div>
            <p>Préparation de votre tableau de bord…</p>
          </div>
        ) : dashboard.isError && !data ? (
          <div className="dash-error" role="alert">
            <AlertCircleIcon />
            <h2>Le tableau de bord est indisponible</h2>
            <p>Les indicateurs n’ont pas pu être chargés. Réessayez dans quelques instants.</p>
            <Button
              size="sm"
              onClick={() => {
                void dashboard.refetch();
              }}
            >
              Réessayer
            </Button>
          </div>
        ) : data ? (
          <div
            key={scopeKey}
            className={`dash-section dash-section--${section}`}
            aria-busy={dashboard.isFetching}
          >
            {dashboard.isError && (
              <div className="dash-refresh-error" role="alert">
                L’actualisation a échoué. Les dernières données chargées restent affichées.
              </div>
            )}
            <div className="dash-section-heading">
              <div>
                <h2>{content.title}</h2>
              </div>
              <Link to={content.link}>
                {section === 'overview' && <PlusIcon />}
                {content.linkLabel}
                <ArrowRightIcon />
              </Link>
            </div>
            <DashboardMetrics metrics={data.metrics} />
            {data.ratios.length > 0 && <DashboardRatios ratios={data.ratios} />}
            <div className="dash-chart-grid">
              {data.charts.map((chart) => (
                <DashboardChartCard key={chart.id} chart={chart} />
              ))}
            </div>
            {data.activities.length > 0 && <DashboardActivities groups={data.activities} />}
            <footer className="dash-methodology">
              <details>
                <summary>Comprendre les indicateurs</summary>
                <ul>
                  {data.notes.map((note) => (
                    <li key={note}>{note}</li>
                  ))}
                </ul>
              </details>
              <span>
                Actualisé à{' '}
                {new Date(data.generatedAt).toLocaleTimeString('fr-FR', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </footer>
          </div>
        ) : null}
      </TabPanel>
    </div>
  );
};
